import { Fragment, useMemo, useState } from "react";
import Close from "@mui/icons-material/Close";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Refresh from "@mui/icons-material/Refresh";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  isSncrRetencaoTipo,
  SNCR_CONSELHOS,
  SNCR_NOTIFICACAO_QTD,
  SNCR_NUMERACAO_TIPOS,
  SNCR_RETENCAO_QTD,
  sncrQuotaTooltip,
  sncrTipoLabel,
} from "../constants";
import {
  summarizeSncrQuota,
  type SncrNumeracaoBloco,
} from "../helpers/basicExtensions";
import { useSncrNumeracoesPanel } from "../hooks/useSncrNumeracoesPanel";
import type { SncrConselho, SncrNotificacaoTipo } from "../types";
import { SncrConnectButton } from "./SncrConnectButton";

const cellSx = {
  px: 1,
  py: 0.85,
  fontSize: "0.8125rem",
  whiteSpace: "nowrap" as const,
  verticalAlign: "middle" as const,
};

type TableRow =
  | { kind: "bloco"; id: string; bloco: SncrNumeracaoBloco }
  | { kind: "empty"; id: string; tipo: SncrNotificacaoTipo };

type Props = {
  onClose: () => void;
};

export function SncrNumeracoesPanel({ onClose }: Props) {
  const {
    isConnected,
    loginWithGovBr,
    form,
    setForm,
    loading,
    blocos,
    allBlocos,
    loadingList,
    reload,
    tipoFiltro,
    setTipoFiltro,
    solicitarEAtualizar,
  } = useSncrNumeracoesPanel({ enabled: true });

  const [openSolicitar, setOpenSolicitar] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const quotaForm = useMemo(
    () => summarizeSncrQuota(allBlocos, form.receita),
    [allBlocos, form.receita],
  );

  const cotaPorTipo = useMemo(() => {
    const map = new Map<
      SncrNotificacaoTipo,
      ReturnType<typeof summarizeSncrQuota>
    >();
    for (const tipo of SNCR_NUMERACAO_TIPOS) {
      map.set(tipo, summarizeSncrQuota(allBlocos, tipo));
    }
    return map;
  }, [allBlocos]);

  const tableRows = useMemo((): TableRow[] => {
    const tipos: SncrNotificacaoTipo[] =
      tipoFiltro === "ALL" ? [...SNCR_NUMERACAO_TIPOS] : [tipoFiltro];
    const rows: TableRow[] = [];
    for (const tipo of tipos) {
      const doTipo = allBlocos.filter((b) => b.tipoReceita === tipo);
      if (doTipo.length === 0) {
        rows.push({ kind: "empty", id: `empty-${tipo}`, tipo });
      } else {
        for (const bloco of doTipo) {
          rows.push({ kind: "bloco", id: bloco.id, bloco });
        }
      }
    }
    return rows;
  }, [allBlocos, tipoFiltro]);

  const handleSolicitar = async () => {
    try {
      await solicitarEAtualizar();
      setOpenSolicitar(false);
    } catch {
      // toast no hook
    }
  };

  const openSolicitarTipo = (tipo?: SncrNotificacaoTipo) => {
    const receita =
      tipo ?? (tipoFiltro !== "ALL" ? tipoFiltro : undefined);
    if (receita) {
      setForm((prev) => ({ ...prev, receita }));
    }
    setOpenSolicitar(true);
  };

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}
      >
        <Typography variant="h6" fontWeight={700}>
          Gestão SNCR
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            aria-label="Atualizar"
            onClick={() => void reload()}
            disabled={loadingList}
          >
            <Refresh fontSize="small" />
          </IconButton>
          <IconButton size="small" aria-label="Fechar" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <Box sx={{ p: 2, overflow: "auto", flex: 1, minHeight: 0 }}>
        <Stack spacing={1.5}>
          <SncrConnectButton
            fullWidth
            isConnected={isConnected}
            onConnect={loginWithGovBr}
          />

          <Button
            fullWidth
            variant="contained"
            color="secondary"
            disabled={!isConnected}
            onClick={() => openSolicitarTipo()}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Solicitar numeração
          </Button>

          <FormControl size="small" fullWidth>
            <InputLabel id="sncr-tipo-filtro">Tipo</InputLabel>
            <Select
              labelId="sncr-tipo-filtro"
              label="Tipo"
              value={tipoFiltro}
              onChange={(e) =>
                setTipoFiltro(e.target.value as SncrNotificacaoTipo | "ALL")
              }
            >
              <MenuItem value="ALL">Todos</MenuItem>
              {SNCR_NUMERACAO_TIPOS.map((tipo) => (
                <MenuItem key={tipo} value={tipo}>
                  {sncrTipoLabel(tipo)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="subtitle2" fontWeight={700}>
            Numeração ({tableRows.length})
            {blocos.length > 0 && (
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1, fontWeight: 400 }}
              >
                {blocos.length} bloco{blocos.length === 1 ? "" : "s"}
              </Typography>
            )}
          </Typography>

          {loadingList ? (
            <Stack alignItems="center" py={4}>
              <CircularProgress size={28} />
            </Stack>
          ) : (
            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Box
                component="table"
                sx={{
                  width: "100%",
                  tableLayout: "fixed",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                }}
              >
                <Box component="thead">
                  <Box
                    component="tr"
                    sx={{
                      bgcolor: "action.hover",
                      "& th": {
                        ...cellSx,
                        fontWeight: 700,
                        color: "text.secondary",
                        textAlign: "left",
                        letterSpacing: 0.2,
                      },
                    }}
                  >
                    <Box component="th" sx={{ width: 28 }} />
                    <Box component="th" sx={{ width: "12%" }}>
                      Tipo
                    </Box>
                    <Box component="th" sx={{ width: "36%" }}>
                      Faixa
                    </Box>
                    <Box component="th" sx={{ width: "12%", textAlign: "right" }}>
                      Disp.
                    </Box>
                    <Box component="th" sx={{ width: "12%", textAlign: "right" }}>
                      Usad.
                    </Box>
                    <Box
                      component="th"
                      sx={{ width: "14%", textAlign: "right" }}
                      title="Notificação: cota diária 50. RCE/RET: 1000 por requisição, limite 3000/mês."
                    >
                      Cota
                    </Box>
                    <Box component="th" sx={{ width: "12%", textAlign: "right" }}>
                      Saldo
                    </Box>
                  </Box>
                </Box>
                <Box component="tbody">
                  {tableRows.map((row) => {
                    const open = expandedId === row.id;
                    if (row.kind === "empty") {
                      const cota = cotaPorTipo.get(row.tipo);
                      return (
                        <Fragment key={row.id}>
                          <Box
                            component="tr"
                            onClick={() => toggleExpand(row.id)}
                            sx={{
                              borderTop: 1,
                              borderColor: "divider",
                              cursor: "pointer",
                              opacity: 0.72,
                              bgcolor: open ? "action.selected" : "transparent",
                              "&:hover": { bgcolor: "action.hover", opacity: 1 },
                              "& td": { ...cellSx },
                            }}
                          >
                            <Box component="td" sx={{ px: 0.5, width: 32 }}>
                              <ExpandMore
                                sx={{
                                  fontSize: 18,
                                  transform: open
                                    ? "rotate(0deg)"
                                    : "rotate(-90deg)",
                                  transition: "transform 0.15s",
                                  color: "text.secondary",
                                }}
                              />
                            </Box>
                            <Box component="td" sx={{ fontWeight: 700 }}>
                              <Tooltip title={sncrQuotaTooltip(row.tipo)} arrow>
                                <Box
                                  component="span"
                                  sx={{ borderBottom: "1px dotted", cursor: "help" }}
                                >
                                  {row.tipo}
                                </Box>
                              </Tooltip>
                            </Box>
                            <Box
                              component="td"
                              sx={{ color: "text.secondary", fontStyle: "italic" }}
                            >
                              Não solicitado
                            </Box>
                            <Box
                              component="td"
                              sx={{ textAlign: "right", color: "text.secondary" }}
                            >
                              0
                            </Box>
                            <Box
                              component="td"
                              sx={{ textAlign: "right", color: "text.secondary" }}
                            >
                              0
                            </Box>
                            <Box component="td" sx={{ textAlign: "right" }}>
                              {cota ? (
                                <Tooltip title={sncrQuotaTooltip(row.tipo)} arrow>
                                  <Box
                                    component="span"
                                    sx={{
                                      borderBottom: "1px dotted",
                                      cursor: "help",
                                    }}
                                  >
                                    {cota.restante}/{cota.limite}
                                  </Box>
                                </Tooltip>
                              ) : (
                                "—"
                              )}
                            </Box>
                            <Box
                              component="td"
                              sx={{ textAlign: "right", color: "text.secondary" }}
                            >
                              —
                            </Box>
                          </Box>
                          <Box component="tr">
                            <Box
                              component="td"
                              colSpan={7}
                              sx={{ p: 0, border: 0 }}
                            >
                              <Collapse in={open} timeout="auto" unmountOnExit>
                                <Box
                                  sx={{
                                    px: 1.5,
                                    py: 1.25,
                                    bgcolor: "grey.50",
                                    borderTop: 1,
                                    borderColor: "divider",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mb: 1 }}
                                  >
                                    Nenhuma numeração de{" "}
                                    <strong>{row.tipo}</strong>. Solicite um
                                    lote para usar na prescrição.
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    disabled={!isConnected}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openSolicitarTipo(row.tipo);
                                    }}
                                    sx={{ textTransform: "none", fontWeight: 700 }}
                                  >
                                    Solicitar {row.tipo}
                                  </Button>
                                </Box>
                              </Collapse>
                            </Box>
                          </Box>
                        </Fragment>
                      );
                    }

                    const { bloco } = row;
                    const cota = cotaPorTipo.get(bloco.tipoReceita);
                    return (
                      <Fragment key={row.id}>
                        <Box
                          component="tr"
                          onClick={() => toggleExpand(row.id)}
                          sx={{
                            borderTop: 1,
                            borderColor: "divider",
                            cursor: "pointer",
                            bgcolor: open ? "action.selected" : "transparent",
                            "&:hover": { bgcolor: "action.hover" },
                            "& td": { ...cellSx, fontWeight: 600 },
                          }}
                        >
                          <Box component="td" sx={{ px: 0.5, width: 32 }}>
                            <ExpandMore
                              sx={{
                                fontSize: 18,
                                transform: open
                                  ? "rotate(0deg)"
                                  : "rotate(-90deg)",
                                transition: "transform 0.15s",
                                color: "text.secondary",
                              }}
                            />
                          </Box>
                          <Box component="td">
                            <Tooltip
                              title={sncrQuotaTooltip(bloco.tipoReceita)}
                              arrow
                            >
                              <Box
                                component="span"
                                sx={{ borderBottom: "1px dotted", cursor: "help" }}
                              >
                                {bloco.tipoReceita}
                              </Box>
                            </Tooltip>
                          </Box>
                          <Box
                            component="td"
                            title={`${bloco.inicio} → ${bloco.fim}`}
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontWeight: 400,
                              color: "text.secondary",
                            }}
                          >
                            {bloco.inicio} → {bloco.fim}
                          </Box>
                          <Box
                            component="td"
                            sx={{ textAlign: "right", color: "success.main" }}
                          >
                            {bloco.disponiveis.length}
                          </Box>
                          <Box
                            component="td"
                            sx={{ textAlign: "right", color: "warning.main" }}
                          >
                            {bloco.usados.length}
                          </Box>
                          <Box component="td" sx={{ textAlign: "right" }}>
                            {cota ? (
                              <Tooltip
                                title={sncrQuotaTooltip(bloco.tipoReceita)}
                                arrow
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    borderBottom: "1px dotted",
                                    cursor: "help",
                                  }}
                                >
                                  {cota.restante}/{cota.limite}
                                </Box>
                              </Tooltip>
                            ) : (
                              "—"
                            )}
                          </Box>
                          <Box component="td" sx={{ textAlign: "right" }}>
                            {typeof bloco.saldoReceitas === "number"
                              ? bloco.saldoReceitas
                              : "—"}
                          </Box>
                        </Box>
                        <Box component="tr">
                          <Box
                            component="td"
                            colSpan={7}
                            sx={{ p: 0, border: 0 }}
                          >
                            <Collapse in={open} timeout="auto" unmountOnExit>
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 1.25,
                                  bgcolor: "grey.50",
                                  borderTop: 1,
                                  borderColor: "divider",
                                }}
                              >
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  flexWrap="wrap"
                                  useFlexGap
                                  sx={{ mb: 1 }}
                                >
                                  <Chip
                                    size="small"
                                    label={bloco.status}
                                    color={
                                      bloco.status === "disponivel"
                                        ? "success"
                                        : "default"
                                    }
                                  />
                                  {bloco.lotes.length > 1 && (
                                    <Chip
                                      size="small"
                                      label={`${bloco.lotes.length} lotes`}
                                      variant="outlined"
                                    />
                                  )}
                                </Stack>
                                <Stack
                                  direction="row"
                                  flexWrap="wrap"
                                  useFlexGap
                                  spacing={0.75}
                                >
                                  {bloco.numeros.map((numero) => {
                                    const usado = bloco.usados.includes(numero);
                                    return (
                                      <Chip
                                        key={numero}
                                        size="small"
                                        label={numero}
                                        color={usado ? "default" : "success"}
                                        variant={usado ? "filled" : "outlined"}
                                        sx={{
                                          opacity: usado ? 0.7 : 1,
                                          textDecoration: usado
                                            ? "line-through"
                                            : "none",
                                        }}
                                      />
                                    );
                                  })}
                                </Stack>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ mt: 1, display: "block" }}
                                >
                                  Verde = disponível · Riscado = usado.{" "}
                                  {sncrQuotaTooltip(bloco.tipoReceita)} Saldo
                                  deste bloco.
                                </Typography>
                              </Box>
                            </Collapse>
                          </Box>
                        </Box>
                      </Fragment>
                    );
                  })}
                </Box>
              </Box>
            </Paper>
          )}
        </Stack>
      </Box>

      <Dialog
        open={openSolicitar}
        onClose={() => !loading && setOpenSolicitar(false)}
        fullWidth
        maxWidth="xs"
        disableScrollLock
      >
        <DialogTitle>Solicitar numeração</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {quotaForm.periodo === "mes"
              ? `Restante no mês (${form.receita}): `
              : `Restante hoje (${form.receita}): `}
            <strong>{quotaForm.restante}</strong> / {quotaForm.limite}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
            {sncrQuotaTooltip(form.receita)}
            {isSncrRetencaoTipo(form.receita)
              ? ` Cada solicitação gera até ${SNCR_RETENCAO_QTD.perRequest} números.`
              : ""}
          </Typography>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="sncr-solicitar-tipo">Tipo</InputLabel>
              <Select
                labelId="sncr-solicitar-tipo"
                label="Tipo"
                value={form.receita}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    receita: e.target.value as SncrNotificacaoTipo,
                  }))
                }
              >
                {SNCR_NUMERACAO_TIPOS.map((tipo) => (
                  <MenuItem key={tipo} value={tipo}>
                    <Tooltip title={sncrQuotaTooltip(tipo)} placement="right">
                      <Box component="span" sx={{ width: "100%" }}>
                        {sncrTipoLabel(tipo)}
                      </Box>
                    </Tooltip>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="sncr-solicitar-conselho">Conselho</InputLabel>
              <Select
                labelId="sncr-solicitar-conselho"
                label="Conselho"
                value={form.conselho}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    conselho: e.target.value as SncrConselho,
                  }))
                }
              >
                {SNCR_CONSELHOS.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                label="UF"
                value={form.uf}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    uf: e.target.value.toUpperCase().slice(0, 2),
                  }))
                }
                sx={{ width: 90 }}
              />
              <TextField
                size="small"
                fullWidth
                label="Documento"
                value={form.documento}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    documento: e.target.value.replace(/\D/g, ""),
                  }))
                }
              />
            </Stack>
            {!isSncrRetencaoTipo(form.receita) && (
              <TextField
                size="small"
                type="number"
                label="Quantidade"
                value={form.quantidade}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    quantidade:
                      Number(e.target.value) || SNCR_NOTIFICACAO_QTD.min,
                  }))
                }
                inputProps={{
                  min: SNCR_NOTIFICACAO_QTD.min,
                  max: SNCR_NOTIFICACAO_QTD.max,
                }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenSolicitar(false)}
            disabled={loading}
            sx={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSolicitar}
            disabled={loading}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {loading ? "Solicitando…" : "Solicitar e salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
